import { Controller } from "@hotwired/stimulus"

// Phase 3.8 Tier 2 Step 25 — Google Drive folder picker.
//
// The Stimulus controller lazy-loads `https://apis.google.com/js/api.js`,
// then `gapi.load("picker")`, then mounts a folder-only Picker view
// authorized with the connection's OAuth token. On selection, it writes
// the picked folder's id + name + web URL into hidden form fields and
// updates the read-only display input — the form submit then carries
// the values through to `ProjectIntegrationLinksController#build_config_for`.
//
// We don't bundle Google's SDK; loading from the CDN is the standard
// pattern and matches the listing's `picker_descriptor.script_url`.
// First click triggers the load + mount; subsequent clicks reuse the
// cached `google` global.
export default class extends Controller {
  static values = {
    scriptUrl:    String,
    developerKey: String,
    clientId:     String,
    appId:        String,
    oauthToken:   String,
    mimeType:     String,
  }

  static targets = ["display", "folderIdField", "folderNameField", "folderUrlField"]

  open(event) {
    event.preventDefault()
    if (!this.oauthTokenValue) {
      this._setStatus("Reconnect Google Drive to pick a folder — the OAuth token is missing.")
      return
    }
    this._loadGapi().then(() => this._mountPicker())
                    .catch((err) => this._setStatus(`Couldn't load Drive Picker: ${err.message || err}`))
  }

  _loadGapi() {
    if (window.google && window.google.picker) {
      return Promise.resolve()
    }
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${this.scriptUrlValue}"]`)
      const onApiReady = () => {
        if (!window.gapi) return reject(new Error("gapi not present after script load"))
        window.gapi.load("picker", { callback: () => resolve() })
      }
      if (existing) {
        if (window.gapi) onApiReady()
        else existing.addEventListener("load", onApiReady, { once: true })
        return
      }
      const script = document.createElement("script")
      script.src = this.scriptUrlValue
      script.async = true
      script.defer = true
      script.addEventListener("load", onApiReady, { once: true })
      script.addEventListener("error", () => reject(new Error("script load failed")))
      document.head.appendChild(script)
    })
  }

  _mountPicker() {
    const view = new window.google.picker.DocsView(window.google.picker.ViewId.FOLDERS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true)
      .setMimeTypes(this.mimeTypeValue || "application/vnd.google-apps.folder")

    const builder = new window.google.picker.PickerBuilder()
      .addView(view)
      .setOAuthToken(this.oauthTokenValue)
      .setCallback((data) => this._handlePick(data))

    if (this.developerKeyValue) builder.setDeveloperKey(this.developerKeyValue)
    if (this.appIdValue)        builder.setAppId(this.appIdValue)

    builder.build().setVisible(true)
  }

  _handlePick(data) {
    if (!data || data.action !== window.google.picker.Action.PICKED) return
    const doc = (data.docs || [])[0]
    if (!doc) return
    if (this.hasFolderIdFieldTarget)   this.folderIdFieldTarget.value   = doc.id || ""
    if (this.hasFolderNameFieldTarget) this.folderNameFieldTarget.value = doc.name || ""
    if (this.hasFolderUrlFieldTarget)  this.folderUrlFieldTarget.value  = doc.url || ""
    if (this.hasDisplayTarget)         this.displayTarget.value         = doc.name || ""
  }

  _setStatus(message) {
    if (this.hasDisplayTarget) {
      this.displayTarget.value = message
    } else {
      console.warn("[drive-folder-picker]", message)
    }
  }
}
